import { Chart } from 'chart.js';
import { TreemapController, TreemapElement } from 'chartjs-chart-treemap';
import { throttle } from 'lodash';
import { filter, Subject, takeUntil } from 'rxjs';
import { HttpParams } from '@angular/common/http';
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef, MatDialogState } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';

import { FilterDialogComponent } from './components/shared/filter-dialog/filter-dialog.component';
import { DeviceMaxSize } from './constants/common.constants';
import { Feature } from './models/feature.models';
import { AuthService } from './services/auth.service';
import { FeatureService } from './services/feature.service';
import { StateCountryService } from './services/state-country.service';
import { NgIf } from '@angular/common';
import { HeaderComponent } from './components/shared/header/header.component';
import { MatSidenav, MatSidenavContainer, MatSidenavContent } from '@angular/material/sidenav';
import { NavigationComponent } from './components/shared/navigation/navigation.component';

Chart.register(TreemapController, TreemapElement); // registering TreemapController to support Treemap chart

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [
    NgIf,
    HeaderComponent,
    MatSidenavContainer,
    MatSidenav,
    NavigationComponent,
    MatSidenavContent,
    RouterOutlet,
  ],
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly destroy$: Subject<void> = new Subject<void>();

  private dialogRef?: MatDialogRef<FilterDialogComponent>;

  departmentTitle = '';
  title = 'dr';
  currentRoute = '';

  get isAdmin() {
    return this.authService.isAdmin;
  }

  get isOfficer() {
    return this.authService.isOfficer;
  }

  get loggedUser() {
    return this.authService.loggedUser;
  }

  constructor(
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly matIconRegistry: MatIconRegistry,
    private readonly domSanitizer: DomSanitizer,
    private readonly featureService: FeatureService,
    private readonly authService: AuthService,
    private readonly stateCountryService: StateCountryService,
  ) {
    this.matIconRegistry.setDefaultFontSetClass('material-symbols-outlined');
    this.isOfficer ? (this.navExpanded = false) : true;

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe((event: NavigationEnd) => {
        this.currentRoute = event.url;

        if (event.url.includes('?')) {
          const params = new HttpParams({
            fromString: event.url.split('?')[1],
          });
          const personal = params.get('personal')?.toLowerCase() === 'true';

          this.featureService.setFeatureFlag(Feature.PERSONAL_INFO, personal);
        }
      });

    if (this.isAdmin) {
      this.departmentTitle = 'TD Admin - All Agencies';
    } else {
      this.authService
        .getDepartment()
        .pipe(takeUntil(this.destroy$))
        .subscribe(({ name }) => {
          this.departmentTitle = name;
        });
    }
  }

  ngOnInit(): void {
    this.stateCountryService.load();
  }

  openFilterDialog(): void {
    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = false;
    dialogConfig.autoFocus = true;
    dialogConfig.position = {
      top: '48px', //header
      left: '0',
    };
    dialogConfig.maxWidth = '100vw';
    dialogConfig.width = '100%';
    if (window.innerWidth <= DeviceMaxSize.TABLET_HORIZONTAL) {
      dialogConfig.height = '100%';
    }
    dialogConfig.panelClass = 'filter-dialog';
    dialogConfig.backdropClass = 'filter-dialog__backdrop';
    dialogConfig.restoreFocus = false;

    this.dialogRef = this.dialog.open(FilterDialogComponent, dialogConfig);
  }

  @HostListener('window:resize', ['$event'])
  onResize = throttle(() => {
    if (!this.sidenavOpened && window.innerWidth > DeviceMaxSize.MOBILE) {
      this.sidenavOpened = true;
      this.navExpanded = window.innerWidth > DeviceMaxSize.TABLET_HORIZONTAL;
    }
    this.sidenavOpenable = window.innerWidth <= DeviceMaxSize.MOBILE;

    if (this.dialogRef && this.dialogRef.getState() === MatDialogState.OPEN) {
      this.dialogRef.updateSize('100%', window.innerWidth <= DeviceMaxSize.TABLET_HORIZONTAL ? '100%' : 'auto');
    }
  }, 100);

  sidenavOpened = window.innerWidth > DeviceMaxSize.MOBILE;
  sidenavOpenable = window.innerWidth <= DeviceMaxSize.MOBILE;
  navExpanded =
    window.innerWidth > DeviceMaxSize.TABLET_HORIZONTAL ||
    window.innerWidth <= DeviceMaxSize.MOBILE ||
    !this.authService.isOfficer;

  toggleSideNav(): void {
    this.sidenavOpened = !this.sidenavOpened;
  }

  closeSideNav(): void {
    this.sidenavOpened = false;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
